<template>
  <div ref="rootRef" class="prof-root">
    <button
      type="button"
      class="prof-row"
      :class="{ rail: collapsed }"
      aria-label="Mon profil et mes réglages"
      :aria-expanded="open ? 'true' : 'false'"
      @click="open = !open"
    >
      <span class="prof-avatar">{{ initials }}</span>
      <span class="prof-col">
        <span class="prof-name">{{ displayName }}</span>
        <span class="prof-role">{{ displayRole }}</span>
      </span>
      <span class="prof-rlabel">Profil</span>
      <StrokeIcon class="prof-chev" :paths="open ? ICONS.chevDown : ICONS.chevUp" :size="15" :stroke-width="2" />
    </button>

    <div v-if="open" class="prof-menu" :class="{ rail: collapsed }">
      <div class="prof-head">
        <span class="prof-mname">{{ displayName }}</span>
        <span v-if="user?.email && user.email !== displayName" class="prof-msub">{{ user.email }}</span>
        <span v-if="user?.ndc" class="prof-msub">Identifiant : <strong>{{ user.ndc }}</strong></span>
        <span class="prof-rolepill">{{ displayRole }}</span>
      </div>
      <div class="prof-div"></div>
      <router-link to="/email-preferences" class="prof-item" @click="open = false">
        <StrokeIcon :paths="ICONS.mail" :size="17" />
        Préférences email
      </router-link>
      <template v-if="isDevOrStaging">
        <div class="prof-div"></div>
        <div class="prof-lbl">Outils de développement</div>
        <router-link to="/debug" class="prof-item" @click="open = false">
          <StrokeIcon :paths="ICONS.clock" :size="17" />
          Horloge système
        </router-link>
        <router-link to="/debug/emails" class="prof-item" @click="open = false">
          <StrokeIcon :paths="ICONS.mailOpen" :size="17" />
          Emails interceptés
        </router-link>
        <router-link to="/debug/sms" class="prof-item" @click="open = false">
          <StrokeIcon :paths="ICONS.sms" :size="17" />
          SMS interceptés
        </router-link>
        <router-link to="/debug/clean-db" class="prof-item" @click="open = false">
          <StrokeIcon :paths="ICONS.db" :size="17" />
          Nettoyage BDD
        </router-link>
      </template>
      <div class="prof-div"></div>
      <button type="button" class="prof-item out" @click="handleLogout">
        <StrokeIcon :paths="ICONS.logout" :size="17" />
        Déconnexion
      </button>
    </div>
  </div>
</template>

<script setup>
/**
 * Profil de la barre v2 (canvas « Barre de navigation v2 ») : avatar à
 * initiales + nom + rôle, menu ancré au-dessus du pied avec identité
 * complète (email, identifiant NDC, badge de rôle), Préférences email,
 * outils réservés au dev & staging, déconnexion.
 */
import { ref, computed } from 'vue'
import { useClickOutside } from '../../composables/useClickOutside.js'
import { formatUserName } from '../../utils/formatUserName.js'
import { roleShortLabel } from '../../constants/roles.js'
import { StrokeIcon } from '../ui'

const props = defineProps({
  user: { type: Object, default: null },
  collapsed: { type: Boolean, default: false }
})

const emit = defineEmits(['logout'])

const ICONS = {
  chevUp: ['M6 15l6-6 6 6'],
  chevDown: ['M6 9l6 6 6-6'],
  mail: ['M4 5h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2z', 'M22 7l-10 6L2 7'],
  mailOpen: ['M2 9l10-6 10 6v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V9z', 'M2 9l10 7 10-7'],
  clock: ['M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z', 'M12 7v5l3 2'],
  sms: ['M21 11.5a8.4 8.4 0 0 1-9 8.4 8.6 8.6 0 0 1-3.8-.9L3 21l2-5.2a8.4 8.4 0 1 1 16-4.3z', 'M8.5 12h.01M12.5 12h.01M16.5 12h.01'],
  db: ['M12 8c5 0 8-1.3 8-3s-3-3-8-3-8 1.3-8 3 3 3 8 3z', 'M4 5v14c0 1.7 3 3 8 3s8-1.3 8-3V5', 'M4 12c0 1.7 3 3 8 3s8-1.3 8-3'],
  logout: ['M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4', 'M16 17l5-5-5-5', 'M21 12H9']
}

const open = ref(false)
const rootRef = ref(null)
const isDevOrStaging = import.meta.env.MODE !== 'production' || import.meta.env.VITE_IS_STAGING === 'true'

const displayName = computed(() => {
  if (!props.user) return 'Utilisateur'
  return formatUserName(props.user)
})

// Libellé court (« Super admin ») : la rangée et la pilule du menu sont des
// surfaces denses — le libellé complet y déborde (cf. canvas).
const displayRole = computed(() => {
  if (!props.user?.role) return ''
  return roleShortLabel(props.user.role)
})

const initials = computed(() => {
  const words = displayName.value.trim().split(/\s+/).filter(Boolean)
  if (!words.length) return '?'
  return words.slice(0, 2).map(w => w[0].toUpperCase()).join('')
})

const handleLogout = () => {
  open.value = false
  emit('logout')
}

useClickOutside(rootRef, () => { open.value = false })
</script>

<style scoped>
.prof-root { position: relative; }

.prof-row {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-height: 52px;
  padding: 8px 10px;
  border: 1px solid transparent;
  background: transparent;
  border-radius: 6px;
  font-family: inherit;
  cursor: pointer;
  text-align: left;
  transition: background 150ms;
}
.prof-row:hover { background: var(--color-bg-tertiary); }
.prof-row:focus-visible { outline: none; box-shadow: var(--focus-ring); }

.prof-avatar {
  width: 34px;
  height: 34px;
  border-radius: 9999px;
  background: var(--color-primary-lighter);
  color: var(--color-primary-dark);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  flex-shrink: 0;
}

.prof-col {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.prof-name {
  font-size: 13.5px;
  font-weight: 600;
  color: var(--color-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.prof-role { font-size: 12px; color: var(--color-text-tertiary); }
.prof-chev { color: var(--color-text-muted); flex-shrink: 0; }
.prof-rlabel { display: none; }

.prof-row.rail {
  flex-direction: column;
  gap: 4px;
  padding: 8px 4px;
}
.prof-row.rail .prof-col,
.prof-row.rail .prof-chev { display: none; }
.prof-row.rail .prof-rlabel {
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: var(--color-text-secondary);
}

.prof-menu {
  position: absolute;
  bottom: calc(100% + 8px);
  left: -2px;
  right: -2px;
  background: var(--color-bg-primary);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.14);
  z-index: 80;
  overflow: hidden;
}
.prof-menu.rail {
  left: calc(100% + 10px);
  right: auto;
  width: 280px;
  bottom: 0;
}

.prof-head {
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.prof-mname { font-size: 14px; font-weight: 600; color: var(--color-text-primary); word-break: break-word; }
.prof-msub { font-size: 12.5px; color: var(--color-text-tertiary); word-break: break-word; }
.prof-msub strong {
  font-family: var(--font-family-mono);
  font-size: 12px;
  color: var(--color-text-primary);
  letter-spacing: 0.5px;
  font-weight: 600;
}
.prof-rolepill {
  align-self: flex-start;
  background: var(--color-primary-lighter);
  color: var(--color-primary-dark);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  padding: 3px 10px;
  border-radius: 9999px;
  margin-top: 3px;
}

.prof-div { height: 1px; background: var(--color-border-light); }

.prof-lbl {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--color-text-muted);
  padding: 10px 14px 2px;
}

.prof-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  min-height: 44px;
  font-size: 13.5px;
  font-weight: 500;
  font-family: inherit;
  color: var(--color-text-primary);
  cursor: pointer;
  background: transparent;
  border: 0;
  width: 100%;
  text-align: left;
  text-decoration: none;
  transition: background 150ms;
}
.prof-item:hover { background: var(--color-bg-tertiary); }
.prof-item svg { color: var(--color-text-tertiary); flex-shrink: 0; }
.prof-item.out { color: var(--color-danger-dark); }
.prof-item.out svg { color: var(--color-danger-dark); }
.prof-item.out:hover { background: var(--color-danger-lightest); }
</style>
